import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { track, setTag } from "../analytics";
import { LIGHT_ONLY } from "./lightOnlyRoutes";
import { flushSync } from "react-dom";
import { useLocation } from "react-router-dom";

/*
  Тема сайта. Значения в localStorage только 'light' | 'dark';
  «системная» = ключа нет (setTheme('system') его удаляет).
  Первое значение на <html> ставит инлайн-скрипт в public/index.html —
  здесь мы его только поддерживаем и меняем.
*/

const STORAGE_KEY = "nets_theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";
const THEME_COLOR = { light: "#FFFFFF", dark: "#121212" };
// Цвет кнопки виджета Jivo: приходит инлайновым градиентом из их кабинета,
// поэтому задаётся только через Widget API. Остальная косметика виджета —
// в src/styles/vendors/jivo.scss, и подложка «листика» там обязана совпадать
// с этими значениями.
const JIVO_COLOR = { light: "#EA3E2A", dark: "#D63522" };

// Админка и вход всегда светлые: выбор там не применяется и не пишется.
// Сам список — в ./lightOnlyRoutes: им же пользуется аналитика.
export { LIGHT_ONLY } from "./lightOnlyRoutes";

// Длительность кроссфейда держим в одном месте с CSS: 280ms анимация,
// 320ms — когда снимаем класс фолбэка (запас на старт transition).
const SWITCH_MS = 320;
const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia(REDUCED_QUERY).matches;

const ThemeContext = createContext(null);

// Единственное место, где тема попадает в DOM.
const applyThemeToDom = (next) => {
  document.documentElement.setAttribute("data-theme", next);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[next]);
};

const readStored = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch (e) {
    return null;
  }
};

const readSystem = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia(DARK_QUERY).matches
    ? "dark"
    : "light";

export const ThemeProvider = ({ children }) => {
  // null = явного выбора нет, идём за системой
  const [stored, setStored] = useState(readStored);
  const [system, setSystem] = useState(readSystem);
  const { pathname } = useLocation();

  const forcedLight = LIGHT_ONLY.some((re) => re.test(pathname));
  const theme = forcedLight ? "light" : stored || system;

  // За системной темой следим, только пока пользователь не выбрал сам
  useEffect(() => {
    if (stored || !window.matchMedia) return undefined;
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = (event) => setSystem(event.matches ? "dark" : "light");
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange); // старые Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, [stored]);

  // Атрибут на <html> и цвет строки состояния браузера. Этот эффект
  // покрывает всё, что меняет тему не кликом: первый рендер, системную
  // смену и форс светлой на админских роутах — там анимации быть не должно.
  useEffect(() => {
    applyThemeToDom(theme);
  }, [theme]);

  // Виджет Jivo. Его скрипт загружается отложенно (public/index.html, по idle
  // или первому действию юзера), поэтому колбэк вешаем заранее — Jivo сам
  // вызовет его, когда поднимется. Если колбэк уже кем-то определён, не
  // перетираем, а вызываем следом.
  const themeRef = useRef(theme);

  useEffect(() => {
    const paint = () => {
      try {
        if (window.jivo_api && typeof window.jivo_api.setWidgetColor === "function") {
          window.jivo_api.setWidgetColor(JIVO_COLOR[themeRef.current]);
        }
      } catch (e) {}
    };
    const prev = window.jivo_onLoadCallback;
    window.jivo_onLoadCallback = function () {
      paint();
      if (typeof prev === "function") prev.apply(this, arguments);
    };
    paint(); // если виджет успел загрузиться до монтирования
    return () => {
      window.jivo_onLoadCallback = prev;
    };
  }, []);

  // Смена темы на лету: виджета может ещё не быть — тогда просто выходим,
  // цвет проставит колбэк выше.
  useEffect(() => {
    themeRef.current = theme;
    try {
      if (window.jivo_api && typeof window.jivo_api.setWidgetColor === "function") {
        window.jivo_api.setWidgetColor(JIVO_COLOR[theme]);
      }
    } catch (e) {}
  }, [theme]);

  // Таймер снятия класса фолбэка живёт между вызовами: частые клики не
  // должны оставить класс висеть.
  const switchTimer = useRef(0);
  useEffect(() => () => window.clearTimeout(switchTimer.current), []);

  const setTheme = useCallback(
    (next) => {
      if (forcedLight) return; // на админских роутах выбор не трогаем
      if (next !== "light" && next !== "dark" && next !== "system") return;

      const effective = next === "system" ? readSystem() : next;
      track("theme_change", { theme: next });
      setTag("theme", effective);

      // Атрибут ставим прямо здесь, а не только в эффекте: View Transitions
      // снимает «новый» кадр сразу после колбэка, а эффект к тому моменту
      // ещё не отработает — кроссфейда бы не получилось.
      const commit = () => {
        applyThemeToDom(effective);
        if (next === "system") {
          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch (e) {}
          setStored(null);
          setSystem(readSystem());
          return;
        }
        try {
          localStorage.setItem(STORAGE_KEY, next);
        } catch (e) {}
        setStored(next);
      };

      // Анимировать нечего: цвет тот же или пользователь просил без движения.
      if (effective === theme || prefersReducedMotion()) {
        commit();
        return;
      }

      // Основной путь: кроссфейд всей страницы одним кадром. flushSync —
      // чтобы в «новый» кадр попало и то, что рисуется из JS по теме.
      if (typeof document.startViewTransition === "function") {
        const vt = document.startViewTransition(() => {
          flushSync(commit);
        });
        // Переход могут отменить (скрытая вкладка, второй клик подряд) —
        // тема к этому моменту уже применена, гасим отказ промисов, чтобы
        // он не всплывал в консоль.
        if (vt) {
          if (vt.ready && vt.ready.catch) vt.ready.catch(() => {});
          if (vt.finished && vt.finished.catch) vt.finished.catch(() => {});
        }
        return;
      }

      // Фолбэк: на время переключения вешаем класс, под ним у всего
      // появляется transition по цветам. Снимаем по таймеру — элементов
      // слишком много, чтобы ловить transitionend.
      const root = document.documentElement;
      root.classList.add("theme-switching");
      commit();
      window.clearTimeout(switchTimer.current);
      switchTimer.current = window.setTimeout(
        () => root.classList.remove("theme-switching"),
        SWITCH_MS
      );
    },
    [forcedLight, theme]
  );

  const value = useMemo(
    () => ({ theme, setTheme, isSystem: !stored, forcedLight }),
    [theme, setTheme, stored, forcedLight]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme вызван вне ThemeProvider");
  return ctx;
};
